import { ReservationDetails } from "@/components/ReservationDetails/ReservationDetails";
import useDevice from "@/hooks/useDevice";
import useReservation from "@/hooks/useReservation";
import useReservationUI from "@/hooks/useReservationUI";
import SearchBar from "@/pages/ReservationPage/components/SearchBar";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { parseISO, startOfMonth } from "date-fns";
import { useCallback, useMemo, useState } from "react";
import { ReservationCalendar } from "../../components/ReservationCalendar";
import { PendingReservationsCollapse } from "./components/PendingReservationsCollapse";
import { RegularReservationList } from "./components/RegularReservationList";

export default function ReservationsPage() {
  const { pendingReservations, pendingReservationsCount, reservationsById } =
    useReservation();

  const { isMobile } = useDevice();

  const { listRef, setCalendarMonth } = useReservationUI();

  const [selectedReservationId, setSelectedReservationId] = useState<
    string | null
  >(null);

  const selectReservation = useCallback(
    (id: string) =>
      setSelectedReservationId((previousId) =>
        previousId === id ? null : (id ?? null),
      ),
    [setSelectedReservationId],
  );

  const selectedReservation = useMemo(
    () =>
      selectedReservationId ? reservationsById[selectedReservationId]! : null,
    [reservationsById, selectedReservationId],
  );

  const handleShowCalendar = useCallback(() => {
    const dateString = selectedReservation?.group?.date;
    if (dateString) {
      setCalendarMonth(startOfMonth(parseISO(dateString)));
    }
    setSelectedReservationId(null);
  }, [selectedReservation, setCalendarMonth]);

  return (
    <Box
      sx={{
        height: "100%",
        width: "100%",
      }}
    >
      <Stack
        direction="row"
        gap={"32px"}
        sx={{
          height: "100%",
        }}
      >
        <Stack
          direction="column"
          sx={{
            flexShrink: 0,
            width: isMobile ? "100%" : "380px",
            height: "100%",
            minHeight: 0,
            overflowY: isMobile ? "auto" : "hidden",
            overflowX: "hidden",
            borderRight: isMobile
              ? undefined
              : "1px solid rgba(255, 255, 255, 0.12)",
          }}
        >
          {!isMobile && <SearchBar />}
          <PendingReservationsCollapse
            pendingReservations={pendingReservations}
            pendingReservationsCount={pendingReservationsCount}
            selectReservation={selectReservation}
            selectedReservation={selectedReservation}
          />
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <RegularReservationList
              listRef={listRef}
              selectReservation={selectReservation}
              selectedReservation={selectedReservation}
            />
          </Box>
        </Stack>
        {!isMobile && (
          <Box
            data-testid="reservation-detail-pane"
            sx={{
              flexGrow: 1,
              paddingRight: "32px",
              paddingTop: "16px",
              paddingBottom: "16px",
              height: "100%",
              minHeight: 0,
              overflowX: "hidden",
              overflowY: "auto",
            }}
          >
            {selectedReservation ? (
              <ReservationDetails
                key={selectedReservation.id}
                reservation={selectedReservation}
                goToCalendar={handleShowCalendar}
              />
            ) : (
              <ReservationCalendar selectReservation={selectReservation} />
            )}
          </Box>
        )}
      </Stack>
    </Box>
  );
}
